import {
  Position,
  GeoJsonProperties,
  Feature,
  Polygon,
  MultiPolygon,
  Point,
  BBox,
} from "geojson";
import * as turf from "@turf/turf";
import polylabel from "polylabel";
import invariant from "tiny-invariant";
import * as polyclip from "polyclip-ts";
import proj4 from "proj4";
import { every, isArray, isObject } from "lodash";
import { arrayLib } from "@larki/shared/array";
import { ShapeVertex } from "./shape";

const squaredDistance = (a: Position, b: Position) => {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
};

const midpoint = (a: Position, b: Position) => {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
};

/**
 * turf.difference but keeps the properties of the 1st argument
 */
const difference = <P extends GeoJsonProperties>(
  a: Feature<Polygon | MultiPolygon, P>,
  b: Feature<Polygon | MultiPolygon>
): Feature<Polygon | MultiPolygon, P> | null => {
  const diff = turf.difference(turf.featureCollection([a, b]));
  if (!diff) return null;
  const diff2: Feature<Polygon | MultiPolygon, P> = {
    ...diff,
    properties: a.properties,
  };
  return diff2;
};

const getCoords = (geometry: Feature<Point>[]) => {
  return geometry.map((p) => p.geometry.coordinates);
};

const union = <P extends GeoJsonProperties>(
  features: Feature<Polygon | MultiPolygon, P>[],
  properties: P
) => {
  const union: MultiPolygon["coordinates"] | null = polyclip.union(
    // @ts-expect-error can't confirm that there is at least one coord here
    ...features.map((f) => f.geometry.coordinates)
  );
  return union ? turf.multiPolygon(union, properties) : null;
};

const bboxIntersect = (bbox1: BBox, bbox2: BBox) => {
  const [minLng1, minLat1, maxLng1, maxLat1] = bbox1;
  const [minLng2, minLat2, maxLng2, maxLat2] = bbox2;
  return (
    minLng1 <= maxLng2 &&
    maxLng1 >= minLng2 &&
    minLat1 <= maxLat2 &&
    maxLat1 >= minLat2
  );
};

const PROJECTION = proj4("WGS84", "EPSG:3857");
function _polylabelOnLargestRing(
  feat: Polygon | MultiPolygon | Feature<Polygon | MultiPolygon>
) {
  const geom = feat.type === "Feature" ? feat.geometry : feat;
  let poly: Feature<Polygon>;
  if (geom.type === "MultiPolygon") {
    const largest = geom.coordinates.reduce<{
      area: number;
      poly: Feature<Polygon>;
    } | null>((max, current) => {
      const poly = turf.polygon(current);
      const area = turf.area(poly);
      if (!max || area > max.area) {
        return { area, poly };
      }
      return max;
    }, null);
    invariant(largest);
    poly = largest.poly;
  } else {
    poly = turf.feature(geom);
  }
  const points = poly.geometry.coordinates.map((coords) =>
    coords.map((c) => PROJECTION.forward(c))
  );
  const p = polylabel(points);
  const [cxt, cyt] = PROJECTION.inverse([p[0], p[1]]);
  return { point: turf.point([cxt, cyt]), distance: p.distance };
}

function visualCenter(
  feat: Polygon | MultiPolygon | Feature<Polygon | MultiPolygon>
) {
  return _polylabelOnLargestRing(feat).point;
}

/**
 * Hole-aware visual center. Polylabel is run on all rings (outer + holes),
 * so the point never lands inside a boolean cut. For MultiPolygons the
 * sub-polygon with the largest inscribed-circle radius wins — that's the
 * region with the most open space for a label.
 *
 * Returns { point, distance } where distance is the inscribed-circle
 * radius in meters.
 */
function visualCenterWithHoles(
  feat: Polygon | MultiPolygon | Feature<Polygon | MultiPolygon>
) {
  const geom = feat.type === "Feature" ? feat.geometry : feat;
  const polygonsCoords: Polygon["coordinates"][] =
    geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates];

  let best: { point: Feature<Point>; distance: number } | null = null;
  for (const coords of polygonsCoords) {
    if (coords.length === 0 || coords[0].length === 0) continue;
    const projected = coords.map((ring) =>
      ring.map((c) => PROJECTION.forward(c))
    );
    const p = polylabel(projected);
    const [cxt, cyt] = PROJECTION.inverse([p[0], p[1]]);
    const entry = {
      point: turf.point([cxt, cyt]),
      distance: p.distance,
    };
    if (!best || entry.distance > best.distance) best = entry;
  }
  invariant(best);
  return best;
}

const computePerimeter = (geometry: Polygon | MultiPolygon) => {
  // If it's a MultiPolygon, iterate over each polygon
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.reduce((sum, multiPolygon) => {
      return (
        sum +
        multiPolygon.reduce((innerSum, polygon) => {
          const line = turf.lineString(polygon as [number, number][]);
          return innerSum + turf.length(line, { units: "meters" });
        }, 0)
      );
    }, 0);
  }
  // for Polygon
  return geometry.coordinates.reduce((sum, polygon) => {
    const line = turf.lineString(polygon as [number, number][]);
    return sum + turf.length(line, { units: "meters" });
  }, 0);
};

/**
 * @param shapeVertices - [{type: 'polygon' | 'path' | 'rectangle', coordinates: <Position from geojson>}]
 * @returns FeatureCollection<Point>
 */
const computeVerticesMidpoint = (
  shapeVertices: ShapeVertex[] | ShapeVertex[][]
) => {
  const is3dArray =
    isArray(shapeVertices) &&
    every(
      shapeVertices,
      (subArr) => isArray(subArr) && every(subArr, isObject)
    ); // check if a 3d array

  const getMidpoint = (shapeVertices: ShapeVertex[]) =>
    shapeVertices
      .map((shapeVertex, index, arr) => {
        if (index === arr.length - 1 && shapeVertex.type === "path")
          return null; // Skip last iteration for path - since path geometry is not self closing

        // Get the next coordinate (loop back to first if at the last index)
        const coordinate = shapeVertex.coordinates;
        const nextCoordinate = arr[(index + 1) % arr.length].coordinates;
        if (!coordinate || !nextCoordinate) return null;
        return turf.point(
          geometryLib.midpoint(
            coordinate as Position,
            nextCoordinate as Position
          )
        );
      })
      .filter(arrayLib.filterNonNull);

  /**
   * Shapes with holes/boolean
   * It's gonna be a 3D structure for the data, to separately compute the midpoint for each holes segment
   * E.g. structure - [
   *    [
   *      { "type": "polygon", "coordinates": [144.9637, -37.8116] },
   *      { "type": "polygon", "coordinates": [...] },
   *    ],
   *    [
   *      { "type": "path", "coordinates": [...] },
   *    ]...
   *  ]
   * We need to flatMap to get access to the coordinates
   */
  if (is3dArray) {
    return shapeVertices.flatMap((coords) =>
      getMidpoint(coords as ShapeVertex[])
    );
  }
  return getMidpoint(shapeVertices as ShapeVertex[]);
};

export const geometryLib = {
  squaredDistance,
  midpoint,
  difference,
  getCoords,
  union,
  bboxIntersect,
  visualCenter,
  visualCenterWithHoles,
  computePerimeter,
  computeVerticesMidpoint,
};
